import { useCallback, useEffect, useMemo } from 'react'
import SortMenu from 'components/table/SortMenu'
import Table from 'components/table/Table'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { isPresent, stopPropagation } from 'utils'
import SortIcon from 'components/table/SortIcon'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'
import useSortMenu from 'components/table/useSortMenu'
import usePrevious from 'utils/hooks/usePrevious'

const SortableColumn = ({
  minWidth,
  width,
  className,
  title,
  onSortChange,
  sort,
  sortItem,
  sortMenu,
}) => {
  const singleSortMenu = useSortMenu({
    sortItems: sortItem,
    currentSort: sort,
  })
  const onSortChangeRef = useUpdatingRef(onSortChange)
  const isSingleMenu = isPresent(singleSortMenu.menu)
  const activeItem = useMemo(
    () =>
      (isSingleMenu ? singleSortMenu.activeItem : sortMenu.activeItem) || {},
    [isSingleMenu, singleSortMenu.activeItem, sortMenu.activeItem]
  )

  const isColumnOn = activeItem.name === sort.name

  const prevActiveItem = usePrevious(activeItem)

  useEffect(() => {
    if (
      isPresent(prevActiveItem) &&
      (prevActiveItem.name !== activeItem.name ||
        prevActiveItem.order !== activeItem.order)
    ) {
      onSortChangeRef.current({
        name: activeItem.name,
        order: activeItem.order,
      })
    }
  }, [onSortChangeRef, activeItem, prevActiveItem])

  const handleColumnClick = useCallback(() => {
    if (isColumnOn) {
      ;(isSingleMenu ? singleSortMenu : sortMenu).toggleOrder(activeItem)
    } else {
      onSortChangeRef.current(activeItem)
    }
  }, [
    sortMenu,
    singleSortMenu,
    onSortChangeRef,
    activeItem,
    isColumnOn,
    isSingleMenu,
  ])

  return (
    <Table.Column
      minWidth={minWidth}
      width={width}
      thClassName={classNames('sortable', { sort_on: isColumnOn })}
      className={className}
      onClick={handleColumnClick}
      title={
        <>
          {title}
          {isSingleMenu ? (
            <SortIcon
              sortItem={singleSortMenu.activeItem}
              onClick={stopPropagation(() =>
                singleSortMenu.toggleOrder(activeItem)
              )}
            />
          ) : (
            <SortMenu sortMenu={sortMenu} />
          )}
        </>
      }
    />
  )
}

export default SortableColumn

SortableColumn.propTypes = {
  width: PropTypes.number,
  minWidth: PropTypes.number,
  className: PropTypes.string,
  title: PropTypes.node,
  onSortChange: PropTypes.func,
  sort: PropTypes.shape({
    name: PropTypes.string,
    order: PropTypes.string,
  }),
  sortItem: PropTypes.shape({
    name: PropTypes.string,
    defaultOrder: PropTypes.string,
  }),
  sortMenu: useSortMenu.PropType,
}
